import { useL10n } from "@ews/react-localization-context";

import
{
    authContext
} from "@ews/react-auth-context";

import { FC, useEffect } from "react";
import
{
    MenuLink,
    MenuSpacer,
    Sidebar,
    SidebarSection
} from "./Sidebar";

import
{
    RouteParams,
    useNamedRoutes
} from "../../NamedRoutes";

import type {
    MenuToggleProps
} from "../types";


import
{
    useImpersonationStateWithSetter,
    useMyPermissions,
    useMyProfileWithRefresh
} from "../../Login/LoginProvider";

import
{
    UserPermissions,
    permissionAllowed
} from "@ews/react-data";

import
{
    useDoubleConfirmDialog
} from "../../Dialog";

import
{
    useRemoveUser
} from "../../ReactData/user";

import Icon from "@ews/react-icons";
import
{
    ReactClickEvent
} from "../../types";

type SidebarProps = MenuToggleProps & {
    routeParams?: RouteParams;
    title?: string;
    onSimulate: (userId: string) => void;
};


const User: FC<SidebarProps> = ({
    routeParams = {},
    onMenuToggle,
    onSimulate,
    title
}) =>
{
    const { translate: t } = useL10n();
    const { generate, router } = useNamedRoutes();
    const { id: userId } = routeParams;
    const [me, refreshMyProfile] = useMyProfileWithRefresh();
    const dialog = useDoubleConfirmDialog();
    const permissions: UserPermissions = useMyPermissions();
    const removeUser = useRemoveUser();
    const { impersonate, getToken } = authContext.useAuth();
    const [impersonationState, setImpersonationState] = useImpersonationStateWithSetter();

    useEffect(() =>
    {
        if (!router) {
            console.error("User Sidebar: useEffect redirect not possible because router is falsy!");
            return;
        }
        if (impersonationState.impersonationStatus !== "in-progress") {
            // console.log("User Sidebar: useEffect state is NOT in-progress", impersonationState, me);
            return;
        }

        if (me.id === impersonationState.impersonatedUserId) {
            // console.log("User Sidebar: useEffect me.id settled", me.id);
            setImpersonationState({
                ...impersonationState,
                impersonationStatus: "active",

                impersonatedUserName: me.username!,
                impersonatedUserFirstName: me.firstName!,
                impersonatedUserLastName: me.lastName!,
            });

            const redirect = generate('user', { id: me.id });
            // console.log("User Sidebar: useEffect redirect", redirect, router);
            router.push(redirect);
            return;
        }

        return (() =>
        {
            // console.log("User Sidebar: useEffect cleanup", impersonationState, me);
        });
    }, [generate, impersonationState, me, router, setImpersonationState]);

    const mayDelete = userId !== me.id;

    const handleDelete = async (e: ReactClickEvent) =>
    {
        if (!userId) return;

        const response = await dialog({
            title: t("[REACT_REMOVE_USER]"),
            message: t("[CONFIRM_REMOVE_USER]"),
            areYouSureButtonTitle: t("COM_TEMPLATES_ARE_YOU_SURE"),
            confirmButtonTitle: t("COM_FACPMANAGEMENT_REGRESSION_STATUS_OK"),
            cancelButtonTitle: t("COM_BANNERS_CANCEL")
        });

        if (response) {
            await removeUser(userId);
            onMenuToggle && onMenuToggle(e);
        }
    };

    const handleSimulate = async (e: ReactClickEvent) =>
    {
        if (!userId) {
            console.error("Sidebar/User.tsx handleSimulate was called with a falsy userId");
            return;
        }
        if (!impersonate) {
            console.error("Sidebar/User.tsx handleSimulate was called but the impersonate function is falsy!");
            return;
        }

        if (!me?.id) {
            console.error("Sidebar/User.tsx handleSimulate was called but me.id is falsy!");
            return;
        }

        if (impersonationState.impersonationStatus !== "inactive") {
            alert("Simulieren mit simulieren Benutzer ist nicht erlaubt!");
            return;
        }

        const response = await dialog({
            title: t("[REACT_SIMULATE_USER]"),
            message: t("[REACT_SIMULATION_CONFIRM_SIMULATE_USER]"),
            areYouSureButtonTitle: t("COM_TEMPLATES_ARE_YOU_SURE"),
            confirmButtonTitle: t("COM_FACPMANAGEMENT_REGRESSION_STATUS_OK"),
            cancelButtonTitle: t("COM_BANNERS_CANCEL")
        });

        if (response) {
            // console.log(`Sidebar/User.tsx handleSimulate was called and user confirmed. Impersonating ${userId} now.`);
            onSimulate && onSimulate(userId);

            const saveToken = getToken();
            setImpersonationState({
                impersonationStatus: "in-progress",
                impersonatedUserId: userId,
                realUserId: me.id,
                originalToken: saveToken
            });

            await impersonate(userId);

            refreshMyProfile();
        }
    };

    return (
        <Sidebar title={title} onMenuToggle={onMenuToggle}>
            <SidebarSection grow>
                {/* <AcquireAuthorization onMenuToggle={onMenuToggle} /> */}
                {permissionAllowed(permissions.createUser!) ? <MenuLink
                    title={t('[SID_MNU_TAB_SETTINGS]')}
                    onClick={onMenuToggle}
                    link={generate('user', routeParams)}
                    icon={<Icon icon="settings" />}
                /> : <></>}
                {/* <Feature feature="SIMULATE_USER"> */}
                {permissionAllowed(permissions.createUser!) && <MenuLink
                    title={t('[REACT_SIMULATE_LOGIN]')}
                    onClick={handleSimulate}
                    icon={<Icon icon="simulation" />}
                />}
                {/* </Feature> */}
                <MenuLink
                    title={t('[REACT_SEND_NOTIFICATION]')}
                    onClick={onMenuToggle}
                    icon={<Icon icon="list" />}
                    link={generate('user:sent-notifications-log', routeParams)}
                />

                {(permissionAllowed(permissions.createUser!) && mayDelete) ? <>
                    <MenuSpacer />
                    <MenuLink
                        title={t('[SID_MNU_HK_DELETE]')}
                        onClick={handleDelete}
                        icon={<Icon icon="delete" />}
                    /></> : <></>}
            </SidebarSection>
        </Sidebar>
    );

};

export default User;
